import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CoursesPage.css';
import DotGrid from './DotGrid';
import { supabase } from './supabaseClient';
import FloatingCart from './components/FloatingCart';
import Navbar from './components/Navbar';

export default function CoursesPage() {
  const [activeFilter, setActiveFilter] = useState('All Courses');
  const [openBranches, setOpenBranches] = useState(['CSE & IT']);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [showAll, setShowAll] = useState(false);

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCourses();
  }, []);

  useEffect(() => {
    setShowAll(false);
  }, [searchQuery, activeCategory, activeFilter]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('courses')
        .select('*');

      if (error) throw error;
      setCourses(data || []);
    } catch (err) {
      console.error('Error fetching courses:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleBranch = (branch) => {
    setOpenBranches(prev =>
      prev.includes(branch) ? prev.filter(b => b !== branch) : [...prev, branch]
    );
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (course.description && course.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    let matchesType = true;
    if (activeFilter === 'Short Courses/Oneshots') {
      matchesType = course.title.toLowerCase().includes('oneshot') || course.title.toLowerCase().includes('short');
    } else if (activeFilter === 'Detailed Courses') {
      matchesType = !course.title.toLowerCase().includes('oneshot') && !course.title.toLowerCase().includes('short');
    }

    let matchesCategory = true;
    if (activeCategory !== 'All') {
      const catMatches = course.category ? course.category.toLowerCase() : '';
      
      if (activeCategory === 'Programming') {
        matchesCategory = catMatches === 'programming' || catMatches === 'c language' || catMatches === 'python';
      } else if (activeCategory === 'Electrical & Electronics') {
        matchesCategory = catMatches === 'electrical & electronics' || catMatches === 'basic electrical' || catMatches === 'basic electronics';
      } else {
        matchesCategory = catMatches === activeCategory.toLowerCase();
      }
    }

    return matchesSearch && matchesType && matchesCategory;
  }).sort((a, b) => {
    if (a.is_published && !b.is_published) return -1;
    if (!a.is_published && b.is_published) return 1;
    return 0;
  });

  const displayedCourses = showAll ? filteredCourses : filteredCourses.slice(0, 5);

  return (
    <div className="courses-page-container">
      <div className="cp-bg-wrapper">
        <DotGrid
          dotSize={2}
          gap={15}
          baseColor="#004f54"
          activeColor="#00e5c8"
          proximity={120}
          shockRadius={250}
          shockStrength={5}
          resistance={750}
          returnDuration={1.5}
        />
      </div>
      {/* Landing Page Nav */}
      <Navbar />

      <main className="cp-main">
        {/* Header Section */}
        <section className="cp-header-section">
          <div>
            <h1 className="cp-title"><span className="cp-gradient-text">Courses</span> Section</h1>
            <p className="cp-subtitle">
              Access premium lectures, Learn Things completely in details, and curated resources for every engineering discipline.
            </p>
          </div>
          <div className="cp-search-wrapper">
            <svg className="cp-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input 
              type="text" 
              className="cp-search-input" 
              placeholder="Search specific units or topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </section>

        <div className="cp-layout">
          {/* Sidebar */}
          <aside className="cp-sidebar">
            <div className="glass-panel cp-explorer">
              <h3 onClick={() => setActiveCategory('All')} style={{ cursor: 'pointer', color: activeCategory === 'All' ? 'var(--cyan)' : 'var(--ink)', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>account_tree</span>
                  EXPLORER
                </span>
                {activeCategory !== 'All' && (
                  <span className="material-symbols-outlined" style={{ fontSize: '1.1rem', color: 'var(--rose)' }} title="Clear Filter">filter_alt_off</span>
                )}
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* CSE */}
                <div>
                  <button className="cp-branch-btn" onClick={() => { toggleBranch('CSE & IT'); setActiveCategory('Programming'); }}>
                    <div className="cp-branch-icon">
                      <span className="material-symbols-outlined" style={{ color: activeCategory === 'Programming' ? 'var(--cyan)' : '#c0c1ff', fontSize: '1.2rem' }}>terminal</span>
                      <span style={{ color: activeCategory === 'Programming' ? 'var(--cyan)' : 'inherit', fontWeight: activeCategory === 'Programming' ? 'bold' : 'normal' }}>Programming</span>
                    </div>
                    <span>{openBranches.includes('CSE & IT') ? <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--muted)' }}>expand_more</span> : <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--muted)' }}>chevron_right</span>}</span>
                  </button>
                  {openBranches.includes('CSE & IT') && (
                    <div style={{ paddingLeft: '2.5rem', borderLeft: '1px solid var(--border)', marginLeft: '0.6rem', marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                      <span 
                        onClick={() => setActiveCategory('C Language')} 
                        style={{ fontSize: '0.9rem', color: activeCategory === 'C Language' ? 'var(--cyan)' : 'var(--muted)', fontWeight: activeCategory === 'C Language' ? 'bold' : 'normal', cursor: 'pointer' }}>
                        C Language
                      </span>
                      <span 
                        onClick={() => setActiveCategory('Python')} 
                        style={{ fontSize: '0.9rem', color: activeCategory === 'Python' ? 'var(--cyan)' : 'var(--muted)', fontWeight: activeCategory === 'Python' ? 'bold' : 'normal', cursor: 'pointer' }}>
                        Python
                      </span>
                    </div>
                  )}
                </div>

                {/* Mechanical */}
                <div>
                  <button className="cp-branch-btn" onClick={() => setActiveCategory('Mechanics')}>
                    <div className="cp-branch-icon">
                      <span className="material-symbols-outlined" style={{ color: activeCategory === 'Mechanics' ? 'var(--cyan)' : '#c0c1ff', fontSize: '1.2rem' }}>settings_suggest</span>
                      <span style={{ color: activeCategory === 'Mechanics' ? 'var(--cyan)' : 'inherit', fontWeight: activeCategory === 'Mechanics' ? 'bold' : 'normal' }}>Mechanics</span>
                    </div>
                    <span><span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--muted)' }}>chevron_right</span></span>
                  </button>
                </div>

                {/* Civil */}
                <div>
                  <button className="cp-branch-btn" onClick={() => setActiveCategory('Graphics & Drawing')}>
                    <div className="cp-branch-icon">
                      <span className="material-symbols-outlined" style={{ color: activeCategory === 'Graphics & Drawing' ? 'var(--cyan)' : '#c0c1ff', fontSize: '1.2rem' }}>architecture</span>
                      <span style={{ color: activeCategory === 'Graphics & Drawing' ? 'var(--cyan)' : 'inherit', fontWeight: activeCategory === 'Graphics & Drawing' ? 'bold' : 'normal' }}>Graphics &amp; Drawing</span>
                    </div>
                    <span><span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--muted)' }}>chevron_right</span></span>
                  </button>
                </div>

                {/* Electrical */}
                <div>
                  <button className="cp-branch-btn" onClick={() => { toggleBranch('Electrical'); setActiveCategory('Electrical & Electronics'); }}>
                    <div className="cp-branch-icon">
                      <span className="material-symbols-outlined" style={{ color: activeCategory === 'Electrical & Electronics' ? 'var(--cyan)' : '#c0c1ff', fontSize: '1.2rem' }}>electric_bolt</span>
                      <span style={{ color: activeCategory === 'Electrical & Electronics' ? 'var(--cyan)' : 'inherit', fontWeight: activeCategory === 'Electrical & Electronics' ? 'bold' : 'normal' }}>Electrical &amp; Electronics</span>
                    </div>
                    <span>{openBranches.includes('Electrical') ? <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--muted)' }}>expand_more</span> : <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--muted)' }}>chevron_right</span>}</span>
                  </button>
                  {openBranches.includes('Electrical') && (
                    <div style={{ paddingLeft: '2.5rem', borderLeft: '1px solid var(--border)', marginLeft: '0.6rem', marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                      <span 
                        onClick={() => setActiveCategory('Basic Electrical')} 
                        style={{ fontSize: '0.9rem', color: activeCategory === 'Basic Electrical' ? 'var(--cyan)' : 'var(--muted)', fontWeight: activeCategory === 'Basic Electrical' ? 'bold' : 'normal', cursor: 'pointer' }}>
                        Basic Electrical
                      </span>
                      <span 
                        onClick={() => setActiveCategory('Basic Electronics')} 
                        style={{ fontSize: '0.9rem', color: activeCategory === 'Basic Electronics' ? 'var(--cyan)' : 'var(--muted)', fontWeight: activeCategory === 'Basic Electronics' ? 'bold' : 'normal', cursor: 'pointer' }}>
                        Basic Electronics
                      </span>
                    </div>
                  )}
                </div>
                {/* Mathematics */}
                <div>
                  <button className="cp-branch-btn" onClick={() => setActiveCategory('Mathematics')}>
                    <div className="cp-branch-icon">
                      <span className="material-symbols-outlined" style={{ color: activeCategory === 'Mathematics' ? 'var(--cyan)' : '#c0c1ff', fontSize: '1.2rem' }}>calculate</span>
                      <span style={{ color: activeCategory === 'Mathematics' ? 'var(--cyan)' : 'inherit', fontWeight: activeCategory === 'Mathematics' ? 'bold' : 'normal' }}>Mathematics</span>
                    </div>
                    <span><span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--muted)' }}>chevron_right</span></span>
                  </button>
                </div>

                {/* English */}
                <div>
                  <button className="cp-branch-btn" onClick={() => setActiveCategory('English')}>
                    <div className="cp-branch-icon">
                      <span className="material-symbols-outlined" style={{ color: activeCategory === 'English' ? 'var(--cyan)' : '#c0c1ff', fontSize: '1.2rem' }}>translate</span>
                      <span style={{ color: activeCategory === 'English' ? 'var(--cyan)' : 'inherit', fontWeight: activeCategory === 'English' ? 'bold' : 'normal' }}>English</span>
                    </div>
                    <span><span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--muted)' }}>chevron_right</span></span>
                  </button>
                </div>

                {/* Chemistry */}
                <div>
                  <button className="cp-branch-btn" onClick={() => setActiveCategory('Chemistry')}>
                    <div className="cp-branch-icon">
                      <span className="material-symbols-outlined" style={{ color: activeCategory === 'Chemistry' ? 'var(--cyan)' : '#c0c1ff', fontSize: '1.2rem' }}>science</span>
                      <span style={{ color: activeCategory === 'Chemistry' ? 'var(--cyan)' : 'inherit', fontWeight: activeCategory === 'Chemistry' ? 'bold' : 'normal' }}>Chemistry</span>
                    </div>
                    <span><span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--muted)' }}>chevron_right</span></span>
                  </button>
                </div>

                {/* Essential Studies for Career Development */}
                <div>
                  <button className="cp-branch-btn" onClick={() => setActiveCategory('Essential Studies for Career Development')}>
                    <div className="cp-branch-icon">
                      <span className="material-symbols-outlined" style={{ color: activeCategory === 'Essential Studies for Career Development' ? 'var(--cyan)' : '#c0c1ff', fontSize: '1.2rem', flexShrink: 0 }}>work</span>
                      <span style={{ color: activeCategory === 'Essential Studies for Career Development' ? 'var(--cyan)' : 'inherit', fontWeight: activeCategory === 'Essential Studies for Career Development' ? 'bold' : 'normal', textAlign: 'left', lineHeight: '1.2' }}>Essential Studies</span>
                    </div>
                    <span><span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--muted)' }}>chevron_right</span></span>
                  </button>
                </div>
              </div>
            </div>

            {/* Promo Banner */}
            <div className="cp-promo glass-panel">
              <img src="/assets/Firefly.png" alt="Promo" />
              <div className="cp-promo-content">
                <span className="cp-badge-upcoming">UPCOMING</span>
                <h4 style={{ fontFamily: 'var(--font-display)', margin: 0, fontSize: '1.1rem' }}>GATE 2025 Mastery Pack</h4>
              </div>
            </div>
          </aside>

          {/* Main Grid Area */}
          <div>
            <div className="cp-filters-bar glass-panel">
              <div className="cp-filter-chips">
                {['All Courses', 'Short Courses/Oneshots', 'Detailed Courses'].map(f => (
                  <button
                    key={f}
                    className={`cp-chip ${activeFilter === f ? 'active' : ''}`}
                    onClick={() => setActiveFilter(f)}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <div className="cp-sort">
                <span style={{ color: 'var(--muted)', fontSize: '0.85rem', marginRight: '0.5rem' }}>Sort by:</span>
                <select>
                  <option>Latest Uploads</option>
                  <option>Highest Rated</option>
                  <option>Popularity</option>
                </select>
              </div>
            </div>

            <div className="cp-grid">
              {loading ? (
                <>
                  {Array.from({ length: 6 }).map((_, index) => {
                    const isWide = (index + 1) % 3 === 0;
                    return (
                      <div key={index} className={`glass-panel cp-card cp-skeleton-card ${isWide ? 'wide' : ''}`}>
                        <div className="cp-card-img-wrapper skeleton-box"></div>
                        <div className="cp-card-body">
                          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.8rem' }}>
                            <div className="skeleton-box skeleton-badge"></div>
                            <div className="skeleton-box skeleton-badge"></div>
                          </div>
                          <div className="skeleton-box skeleton-title"></div>
                          <div className="skeleton-box skeleton-text"></div>
                          <div className="skeleton-box skeleton-text short"></div>
                          
                          <div className="cp-card-footer" style={{ marginTop: 'auto' }}>
                            <div className="skeleton-box skeleton-instructor"></div>
                            <div className="skeleton-box skeleton-btn"></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : error ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--rose)' }}>
                  <p>Failed to load courses: {error}</p>
                </div>
              ) : filteredCourses.length === 0 ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)', gridColumn: '1 / -1' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}>inventory_2</span>
                  <p>No courses match your filter criteria.</p>
                </div>
              ) : (
                displayedCourses.map((course, index) => {
                  const isWide = (index + 1) % 3 === 0;
                  const discount = course.original_price && course.price ? Math.round((1 - course.price / course.original_price) * 100) : null;

                  return (
                    <Link 
                      to={course.is_published ? `/course/${course.id}` : '#'} 
                      onClick={(e) => { if (!course.is_published) e.preventDefault(); }} 
                      key={course.id} 
                      className={`glass-panel cp-card ${isWide ? 'wide' : ''} ${!course.is_published ? 'cp-card-unpublished' : ''}`} 
                      style={{ textDecoration: 'none' }}>
                    <div className="cp-card-img-wrapper">
                      {course.thumbnail_url ? (
                        <img src={course.thumbnail_url} alt={course.title} style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: 'var(--bg2)' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', background: 'var(--bg2)' }}></div>
                      )}
                      <div className="cp-play-btn">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                      </div>
                      {course.rating && (
                        <div className="cp-rating">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                          {course.rating}
                        </div>
                      )}
                    </div>
                    <div className="cp-card-content">
                      <div className="cp-card-tags">
                        <span className="cp-tag">{course.category || 'COURSE'}</span>
                      </div>
                      <h3 className="cp-card-title">{course.title}</h3>
                      <p className="cp-card-desc">{course.description}</p>
                      <div className="cp-card-footer">
                        <div className="cp-price-wrap">
                          <span className="cp-price-label">Premium Access</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                            <span className="cp-price">
                              {typeof course.price === 'number' ? `₹${(course.price / 180).toFixed(2)}/day` : (course.price || 'Free')}
                            </span>
                            {typeof course.price === 'number' && discount && (
                              <span style={{ fontSize: '0.72rem', color: '#ff4e6a', fontWeight: '800', background: 'rgba(255, 78, 106, 0.12)', padding: '0.15rem 0.5rem', borderRadius: '4px' }}>{discount}% OFF</span>
                            )}
                          </div>
                        </div>
                        {course.is_published ? (
                          <button className="cp-btn">Start Learning</button>
                        ) : (
                          <button className="cp-btn cp-btn-unpublished" disabled>Unavailable</button>
                        )}
                      </div>
                    </div>
                  </Link>
                  );
                })
              )}
            </div>

            {filteredCourses.length > 5 && (
              <div style={{ textAlign: 'center', marginTop: '3rem' }}>
                <button onClick={() => setShowAll(!showAll)} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: '0.85rem', fontWeight: 'bold', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  {showAll ? "Show Less Resources" : "Discover More Resources"}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: showAll ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }}>
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Landing Page Footer */}
      <footer className="em-footer">
        <div className="fg">
          <div className="fb">
            <a href="/" className="em-logo"><span className="l1">ENGI</span><span className="l2">&lt;mitra&gt;</span></a>
            <p>Built for 1st year engineering students. Expert notes, courses, and MPQs for every major Indian university — since 2022.</p>
          </div>
          <div className="fc">
            <h5>Platform</h5>
            <ul>{["Courses", "Notes", "MPQs"].map(l => <li key={l}><a href={`/#${l.toLowerCase()}`}>{l}</a></li>)}</ul>
          </div>
          <div className="fc">
            <h5>Community</h5>
            <ul>
              <li><a href="https://www.instagram.com/engimitra.in" target="_blank" rel="noreferrer">Instagram</a></li>
              <li><a href="https://www.facebook.com/profile.php?id=61587673103498" target="_blank" rel="noreferrer">Facebook</a></li>
              <li><a href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z" target="_blank" rel="noreferrer">WhatsApp Group</a></li>
            </ul>
          </div>
          <div className="fc">
            <h5>Legal</h5>
            <ul>
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms</Link></li>
              <li><Link to="/refund-policy">Refund Policy</Link></li>
              <li><Link to="/contact">Contact Us</Link></li>
            </ul>
          </div>
        </div>
        <div className="fb-bottom">
          <p>© 2026 ENGImitra. All rights reserved.</p>
          <div className="soc-row">
            <a href="https://www.instagram.com/engimitra.in" target="_blank" rel="noreferrer" className="soc" aria-label="Instagram">ig</a>
            <a href="https://www.facebook.com/profile.php?id=61587673103498" target="_blank" rel="noreferrer" className="soc" aria-label="Facebook">fb</a>
            <a href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z" target="_blank" rel="noreferrer" className="soc" aria-label="WhatsApp Group">wa</a>
          </div>
        </div>
      </footer>

      <FloatingCart />
    </div>
  );
}
